import {
  createSlice,
  createSelector,
  createAsyncThunk
} from "@reduxjs/toolkit";
import { logout } from "./auth";
import {
  createCountryOption,
  fetchCountryOptions,
  updateCountryOption
} from "../../services/api-declaration";

// Keeps the HTTP status, so the form can show a 409 (name taken) on the field
const toRejectedValue = (error) => ({
  status: error?.response?.status,
  message: error?.response?.data?.message || error.message
});

// Loads every country option, including hidden ones, so a funnel that uses a
// hidden country still shows its name. Dropdowns use selectVisibleCountryOptions.
export const loadCountryOptions = createAsyncThunk(
  "countryOptions/load",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchCountryOptions({ includeHidden: true });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(toRejectedValue(error));
    }
  }
);

export const createCountry = createAsyncThunk(
  "countryOptions/create",
  async (body, { rejectWithValue }) => {
    try {
      const response = await createCountryOption(body);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(toRejectedValue(error));
    }
  }
);

// Also used to hide and show an option: send { hidden: true | false }
export const updateCountry = createAsyncThunk(
  "countryOptions/update",
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const response = await updateCountryOption(id, body);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(toRejectedValue(error));
    }
  }
);

const sortByOrder = (options) =>
  [...options].sort((a, b) => a.sortOrder - b.sortOrder);

const countryOptionsSlice = createSlice({
  name: "countryOptions",
  initialState: {
    options: [],
    isLoading: false
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadCountryOptions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loadCountryOptions.fulfilled, (state, action) => {
        state.options = action.payload;
        state.isLoading = false;
      })
      .addCase(loadCountryOptions.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(createCountry.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createCountry.fulfilled, (state, action) => {
        state.options = sortByOrder([...state.options, action.payload]);
        state.isLoading = false;
      })
      .addCase(createCountry.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(updateCountry.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateCountry.fulfilled, (state, action) => {
        const updated = action.payload;
        state.options = sortByOrder(
          state.options.map((option) =>
            option._id === updated._id ? updated : option
          )
        );
        state.isLoading = false;
      })
      .addCase(updateCountry.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(logout, (state) => {
        state.options = [];
        state.isLoading = false;
      });
  }
});

export const selectCountryOptions = (state) => state.countryOptions.options;
export const selectIsCountryOptionsLoading = (state) =>
  state.countryOptions.isLoading;

// Only the countries a user may pick in a dropdown
export const selectVisibleCountryOptions = createSelector(
  [selectCountryOptions],
  (options) => options.filter((option) => !option.hidden)
);

export default countryOptionsSlice.reducer;
